import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import { useForceLogout } from "../../hooks/useAccessManagement";

// Kicks every active session for this user, straight from the table — the same action the
// Manage dialog offers, surfaced here so it doesn't need the dialog opened first.
// Confirmed before firing: it interrupts whatever that person is doing right now.
//
// Not a suspension — role and status are untouched and they can sign straight back in.
// Use the Status toggle to actually block someone. Disabled accounts already can't hold a
// session, so the button is inert for them.
export default function ForceLogoutCell({ user }) {
  const { enqueueSnackbar } = useSnackbar();
  const forceLogout = useForceLogout();

  const handleForceLogout = async () => {
    const result = await Swal.fire({
      title: "Sign out everywhere?",
      html: `<div style="font-size: 14px; font-family: 'Quicksand'">
          <b>${user.fullName}</b> will be signed out of every device right now.
          <br/><br/>
          Their role and status won't change — they can sign back in immediately.
        </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, sign out",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#E34C14",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const response = await forceLogout.mutateAsync({ id: user.id });
      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }
      enqueueSnackbar(`${user.fullName.split(" ")[0]} has been signed out everywhere`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Could not force sign-out", { variant: "error" });
    }
  };

  if (forceLogout.isLoading) {
    return <CircularProgress size={14} sx={{ color: "#FF5200", ml: 1 }} />;
  }

  return (
    <Tooltip title={user.isActive ? "Sign this user out of every device" : "Account is disabled — no active sessions"}>
      <span>
        <IconButton size="small" onClick={handleForceLogout} disabled={!user.isActive}>
          <LogoutIcon sx={{ fontSize: 16, color: user.isActive ? "#E34C14" : "#D1D5DB" }} />
        </IconButton>
      </span>
    </Tooltip>
  );
}
